/**
 * Agent Access page: connect a private AI agent (Hermes) to the dashboard with an agent access
 * key. The page issues a single-use setup code inside a copy-paste prompt; the key itself is
 * never shown (docs/plans/agent-access-keys.md). Admin, CEO and Executive only.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { KeyIcon, ClipboardDocumentIcon, CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  AgentKey,
  createSetupCode,
  disconnectAgent,
  getAgentBundleManifest,
  listAgentKeys,
  revokeAgentKey,
} from '../services/agentKeysApi';
import { buildSetupPrompt } from '../utils/agentSetupPrompt';

const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '—');
const formatDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

interface IssuedPrompt {
  prompt: string;
  expiresAt: string;
}

export const AgentAccess: React.FC = () => {
  const [keys, setKeys] = useState<AgentKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('Hermes');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<IssuedPrompt | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setKeys(await listAgentKeys());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const active = keys.find((k) => k.status === 'ACTIVE' && (!k.expiresAt || Date.parse(k.expiresAt) > Date.now()));
  // An unredeemed setup code is a live credential until it expires: always show it, with Cancel.
  const pending = keys.find((k) => k.status === 'PENDING' && !!k.setupExpiresAt && Date.parse(k.setupExpiresAt) > Date.now());
  const history = keys.filter((k) => k !== active && k.status !== 'PENDING');

  const handleConnect = async () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const [manifest, code] = await Promise.all([getAgentBundleManifest(), createSetupCode(name.trim() || 'Hermes')]);
      setIssued({
        prompt: buildSetupPrompt({
          setupCode: code.setupCode,
          origin: window.location.origin,
          file: manifest.file,
          sha256: manifest.sha256,
        }),
        expiresAt: code.setupExpiresAt,
      });
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.prompt);
      setCopied(true);
    } catch {
      setError('Copy failed: select the text in the box and copy it manually.');
    }
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
      setIssued(null);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = (key: AgentKey) => {
    if (!window.confirm(`Disconnect "${key.name}"? The agent loses dashboard access within 5 minutes, and any unused setup prompt stops working.`)) return;
    void run(disconnectAgent);
  };

  const handleCancelSetup = (key: AgentKey) => run(() => revokeAgentKey(key.keyId));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <KeyIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Agent Access</h1>
              <p className="text-gray-500">Connect your private AI agent (Hermes) to the dashboard, read-only, as you.</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>}

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Current connection</h2>
            {loading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : active ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-md border border-green-200 bg-green-50">
                <div className="text-sm text-gray-700 space-y-1">
                  <p className="font-medium text-gray-900">{active.name}</p>
                  <p>Connected {formatDate(active.redeemedAt)} · expires {formatDate(active.expiresAt)}</p>
                  <p>Last used {formatDateTime(active.lastUsedAt)}</p>
                </div>
                <button
                  onClick={() => handleDisconnect(active)}
                  disabled={busy}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No agent is connected.</p>
            )}
            {pending && (
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-md border border-amber-200 bg-amber-50">
                <p className="text-sm text-gray-700">
                  Setup prompt for &quot;{pending.name}&quot; not used yet: it works until {formatDateTime(pending.setupExpiresAt)}.
                </p>
                <button
                  onClick={() => void handleCancelSetup(pending)}
                  disabled={busy}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                >
                  Cancel setup
                </button>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{active ? 'Reconnect or replace' : 'Connect an agent'}</h2>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1 mb-4">
              <li>Create a setup prompt below and copy it.</li>
              <li>Paste it into your Hermes chat and send it. It works once, within 15 minutes.</li>
              <li>When Hermes reports <span className="font-mono">SETUP COMPLETE</span>, send <span className="font-mono">/reload-mcp</span>.</li>
            </ol>
            <p className="text-xs text-gray-500 mb-4">
              You have one connection at a time{active ? ': the current one stops when the new one is set up' : ''}. The agent can only
              read what you can see, and pauses if you do not open this website for 14 days.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                aria-label="Agent name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <button
                onClick={handleConnect}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                Create setup prompt
              </button>
            </div>

            {issued && (
              <div className="mt-4 space-y-2">
                <textarea
                  readOnly
                  value={issued.prompt}
                  rows={8}
                  onFocus={(e) => e.target.select()}
                  aria-label="Setup prompt"
                  className="w-full p-3 font-mono text-xs border border-gray-300 rounded-md bg-gray-50"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-gray-800 text-white hover:bg-gray-900"
                  >
                    {copied ? <CheckIcon className="h-4 w-4 mr-2" /> : <ClipboardDocumentIcon className="h-4 w-4 mr-2" />}
                    {copied ? 'Copied' : 'Copy prompt'}
                  </button>
                  <button
                    onClick={() => void refresh()}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    Check status
                  </button>
                  <span className="text-xs text-gray-500">Setup code expires {formatDateTime(issued.expiresAt)}</span>
                </div>
              </div>
            )}
          </section>

          {history.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Previous connections</h2>
              <ul className="divide-y divide-gray-100 text-sm text-gray-600">
                {history.map((k) => (
                  <li key={k.keyId} className="py-2 flex justify-between gap-3">
                    <span>{k.name}</span>
                    <span>
                      {k.status === 'REVOKED' ? `disconnected ${formatDate(k.revokedAt)}` : `expired ${formatDate(k.expiresAt)}`}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentAccess;
